import axios, { AxiosResponse } from 'axios';

const url = {
  login: '/v1/login',
};

function getAccessToken(): string | null {
  return localStorage.getItem('token');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token');
}

function setAccessToken(token: string) {
  localStorage.setItem('token', token);
}

function setRefreshToken(token: string) {
  localStorage.setItem('refresh_token', token);
}
function redirectToHome(): void {
  window.location.href = '/'; // Redirige a la página de inicio, ajusta según sea necesario
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    console.error('No refresh token available');
    return false;
  }

  try {
    const response: AxiosResponse<{ token: string; refreshToken: string }> =
      await axios.post(`/v1/login`, {
        refreshToken: refreshToken,
      });

    if (response.status === 200 && response.data.token) {
      setAccessToken(response.data.token);
      setRefreshToken(response.data.refreshToken);
      return true;
    } else {
      console.error('Failed to refresh access token');
      redirectToHome();
      return false;
    }
  } catch (error) {
    console.error('Error refreshing access token', error);
    redirectToHome();
    return false;
  }
}
const handleError = (error: any) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data;
    let message = 'An error occurred';
    let code: string | undefined;
    
    // Extraer código y mensaje del error
    if (errorData) {
      // Caso 1: Formato ApiErrorResponse de ms-application (a través de BFF)
      // NestJS BadRequestException retorna: { statusCode: 400, message: "...", error: "CODE" }
      if (typeof errorData === 'object' && 'message' in errorData) {
        message = errorData.message || 'An error occurred';
        // El código puede estar en 'error' (NestJS) o 'code' (directo de ms-application)
        code = errorData.error || errorData.code;
      }
      
      // Caso 2: Mensaje serializado como string JSON (fallback para compatibilidad)
      if (typeof message === 'string' && message.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(message);
          if (parsed.message) {
            message = parsed.message;
          }
          if (parsed.code) {
            code = parsed.code;
          }
        } catch {
          // Si no se puede parsear, usar el mensaje original
        }
      }
    }
    
    return {
      success: false,
      message,
      code, // Incluir código de error para uso en componentes
      status: error.response?.status,
    };
  } else {
    return {
      success: false,
      message: 'An unexpected error occurred',
    };
  }
};

const handleRequestWithTokenRetry = async (
  requestFn: () => Promise<AxiosResponse>
) => {
  try {
    let response = await requestFn();

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: any) {
    if (error.response?.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        try {
          const response = await requestFn();
          return {
            success: true,
            data: response.data,
            status: response.status,
          };
        } catch (retryError) {
          return handleError(retryError);
        }
      } else {
        return handleError(error);
      }
    } else {
      console.error('Request failed', error);
      return handleError(error);
    }
  }
};

export const secureGet = async (url: string) => {
  return handleRequestWithTokenRetry(() =>
    axios.get(url, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
  );
};

export const secureGetFile = async (url: string) => {
  return handleRequestWithTokenRetry(() =>
    axios.get(url, {
      responseType: 'arraybuffer',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
  );
};

export const securePost = async (
  url: string,
  body: any,
  contentType: string = 'application/json',
  responseType: 'json' | 'arraybuffer' = 'json'
) => {
  return handleRequestWithTokenRetry(() =>
    axios.post(url, body, {
      responseType,
      headers: {
        'Content-Type': contentType,
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
  );
};

export const securePut = (
  url: string,
  body: any,
  contentType: string = 'application/json'
) => {
  return handleRequestWithTokenRetry(() =>
    axios.put(url, body, {
      headers: {
        'Content-Type': contentType,
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
  );
};

export const securePatch = (
  url: string,
  body: any,
  contentType: string = 'application/json'
) => {
  return handleRequestWithTokenRetry(() =>
    axios.patch(url, body, {
      headers: {
        'Content-Type': contentType,
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
  );
};

export const secureDelete = (url: string) => {
  return handleRequestWithTokenRetry(() =>
    axios.delete(url, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    })
  );
};

export const publicPost = async (
  url: string,
  body: any,
  contentType: string = 'application/json',
  responseType: 'json' | 'arraybuffer' = 'json'
) => {
  try {
    const response = await axios.post(url, body, {
      headers: {
        'Content-Type': contentType,
      },
      responseType,
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return handleError(error);
  }
};
